import type { HandledBy } from "./liveTranscript";

export interface TranscriptLine {
  speaker: "user" | "assistant";
  mode: "voice" | "typed";
  handledBy: HandledBy;
  text: string;
  startMs?: number;
  endMs?: number;
  atMs?: number;
  delegationId: string | null;
  traceId: string | null;
}

export interface DelegationRecord {
  id: string;
  offsetMs: number;
  requestText: string;
  traceId: string | null;
  outcome: "sent" | "busy";
  stoppedByUser: boolean;
}

export interface BackupRecord {
  id: string;
  order: number;
  liveSessionId: string;
  chatId: string;
  kind: "line" | "delegation";
  data: TranscriptLine | DelegationRecord;
  seq?: number;
}

export interface TranscriptBatch {
  liveSessionId: string;
  seq: number;
  chatId: string;
  lines: TranscriptLine[];
  delegations: DelegationRecord[];
}

export interface PlannedBatch {
  batch: TranscriptBatch;
  ids: string[];
}

export type SendBatch = (batch: TranscriptBatch, keepalive: boolean) => Promise<boolean>;

export function buildBatches(
  records: BackupRecord[],
  nextSeq: Record<string, number>,
): { batches: PlannedBatch[]; nextSeq: Record<string, number>; stamped: BackupRecord[] } {
  const counters = { ...nextSeq };
  const groups = new Map<string, BackupRecord[]>();
  const stamped: BackupRecord[] = [];
  const ordered = records.slice().sort((a, b) => a.order - b.order);
  for (const record of ordered) {
    const key =
      record.seq !== undefined
        ? `${record.liveSessionId}|seq|${record.seq}`
        : `${record.liveSessionId}|chat|${record.chatId}`;
    groups.set(key, [...(groups.get(key) ?? []), record]);
  }
  const batches: PlannedBatch[] = [];
  groups.forEach((group) => {
    const first = group[0];
    let seq = first.seq;
    if (seq === undefined) {
      seq = counters[first.liveSessionId] ?? 1;
      counters[first.liveSessionId] = seq + 1;
      group.forEach((record) => stamped.push({ ...record, seq }));
    }
    const delegations = new Map<string, DelegationRecord>();
    group
      .filter((record) => record.kind === "delegation")
      .forEach((record) => delegations.set((record.data as DelegationRecord).id, record.data as DelegationRecord));
    batches.push({
      batch: {
        liveSessionId: first.liveSessionId,
        seq,
        chatId: first.chatId,
        lines: group.filter((record) => record.kind === "line").map((record) => record.data as TranscriptLine),
        delegations: Array.from(delegations.values()),
      },
      ids: group.map((record) => record.id),
    });
  });
  batches.sort((a, b) => a.batch.seq - b.batch.seq);
  return { batches, nextSeq: counters, stamped };
}

const DB_NAME = "zenvoya_voice";
const RECORDS = "records";
const SEQS = "seqs";

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(RECORDS)) db.createObjectStore(RECORDS, { keyPath: "id" });
        if (!db.objectStoreNames.contains(SEQS)) db.createObjectStore(SEQS, { keyPath: "liveSessionId" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

async function write(store: string, apply: (objectStore: IDBObjectStore) => void): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const transaction = db.transaction(store, "readwrite");
      apply(transaction.objectStore(store));
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function readAll<T>(store: string): Promise<T[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise<T[]>((resolve) => {
    try {
      const request = db.transaction(store, "readonly").objectStore(store).getAll();
      request.onsuccess = () => resolve((request.result as T[]) ?? []);
      request.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

const newId = (): string => crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export class TranscriptBackup {
  private queue: BackupRecord[] = [];
  private nextSeq: Record<string, number> = {};
  private sending = false;
  private failures = 0;
  private retryAt = 0;
  private counter = 0;
  private send: SendBatch;

  constructor(send: SendBatch) {
    this.send = send;
  }

  write(liveSessionId: string, chatId: string, kind: BackupRecord["kind"], data: BackupRecord["data"]): void {
    const record: BackupRecord = {
      id: newId(),
      order: Date.now() * 1000 + (this.counter++ % 1000),
      liveSessionId,
      chatId,
      kind,
      data,
    };
    this.queue.push(record);
    void write(RECORDS, (store) => store.put(record));
  }

  adoptChat(liveSessionId: string, chatId: string): void {
    const adopted: BackupRecord[] = [];
    this.queue = this.queue.map((record) => {
      if (record.liveSessionId !== liveSessionId || record.chatId || record.seq !== undefined) return record;
      const next = { ...record, chatId };
      adopted.push(next);
      return next;
    });
    if (adopted.length > 0) void write(RECORDS, (store) => adopted.forEach((record) => store.put(record)));
  }

  async flush(keepalive = false): Promise<void> {
    if (this.queue.length === 0) return;
    if (!keepalive && (this.sending || Date.now() < this.retryAt)) return;
    const batches = this.plan(this.queue);
    if (keepalive) {
      batches.forEach((planned) => {
        void this.send(planned.batch, true)
          .then((ok) => ok && this.confirm(planned.ids))
          .catch(() => undefined);
      });
      return;
    }
    this.sending = true;
    try {
      for (const planned of batches) {
        const ok = await this.send(planned.batch, false).catch(() => false);
        if (!ok) {
          this.failures += 1;
          this.retryAt = Date.now() + Math.min(30000, 2000 * 2 ** this.failures);
          return;
        }
        this.confirm(planned.ids);
      }
      this.failures = 0;
    } finally {
      this.sending = false;
    }
  }

  async recover(liveSessionId: string | null): Promise<void> {
    const stored = await readAll<BackupRecord>(RECORDS);
    const queued = new Set(this.queue.map((record) => record.id));
    const stale = stored.filter((record) => !queued.has(record.id));
    if (stale.length > 0) {
      const seqs = await readAll<{ liveSessionId: string; next: number }>(SEQS);
      seqs.forEach((row) => {
        if (this.nextSeq[row.liveSessionId] === undefined) this.nextSeq[row.liveSessionId] = row.next;
      });
      for (const planned of this.plan(stale)) {
        const ok = await this.send(planned.batch, false).catch(() => false);
        if (!ok) return;
        void write(RECORDS, (store) => planned.ids.forEach((id) => store.delete(id)));
      }
    }
    const remaining = new Set([...this.queue, ...(await readAll<BackupRecord>(RECORDS))].map((record) => record.liveSessionId));
    const seqs = await readAll<{ liveSessionId: string }>(SEQS);
    const unused = seqs.map((row) => row.liveSessionId).filter((id) => id !== liveSessionId && !remaining.has(id));
    if (unused.length > 0) void write(SEQS, (store) => unused.forEach((id) => store.delete(id)));
  }

  private plan(records: BackupRecord[]): PlannedBatch[] {
    const { batches, nextSeq, stamped } = buildBatches(records, this.nextSeq);
    this.nextSeq = nextSeq;
    if (stamped.length > 0) {
      const seqById = new Map(stamped.map((record) => [record.id, record.seq]));
      this.queue = this.queue.map((record) => (seqById.has(record.id) ? { ...record, seq: seqById.get(record.id) } : record));
      void write(RECORDS, (store) => stamped.forEach((record) => store.put(record)));
      const sessions = Array.from(new Set(stamped.map((record) => record.liveSessionId)));
      void write(SEQS, (store) =>
        sessions.forEach((id) => store.put({ liveSessionId: id, next: this.nextSeq[id] })),
      );
    }
    return batches;
  }

  private confirm(ids: string[]): void {
    const done = new Set(ids);
    this.queue = this.queue.filter((record) => !done.has(record.id));
    void write(RECORDS, (store) => ids.forEach((id) => store.delete(id)));
  }
}
